import {
  BaseFinancialItem,
  FinancialAsset,
  PensionAsset,
} from 'src/financial/financial-data/hooks/useFinancialItems';
import { useState } from 'react';
import { Accordion } from '@mantine/core';
import { IconCoins } from '@tabler/icons-react';
import NetWorthTableSubCategorySection from 'src/financial/financial-data/net-worth-table/NetWorthTableSubCategorySection';
import FinancialAssetsTable from 'src/financial/financial-data/tables/FinancialAssetsTable';
import { AccordionHeader } from 'src/financial/financial-data/net-worth/AccordionHeader';
import { DynamicUiEntitiesMetadata } from 'src/UI/dynamicUI/types';
import PensionsTable from 'src/financial/financial-data/tables/PensionsTable';
import { AssetFeedBadges, AssetFeedIcons } from 'src/financial/financial-data/AssetFeedIndicators';

type AssetsAccordionProps = {
  assets: FinancialAsset[];
  activeItem: string | null;
  onSubmit: () => void;
  metadata: DynamicUiEntitiesMetadata;
  onRowClick: (clickedItem: BaseFinancialItem) => void;
};
export const AssetsAccordion = ({
  assets,
  activeItem,
  onSubmit,
  metadata,
  onRowClick,
}: AssetsAccordionProps) => {
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const accordionKey = 'ASSETS';
  const title = 'Assets';

  const productSections = [
    {
      displayName: 'Cash and investments (fund and direct)',
      id: 'cash-and-investments',
      assets: assets.filter(a => a.product === 'InvestmentAndCash'),
    },
    {
      displayName: 'Pensions',
      id: 'pension',
      assets: assets.filter(a => a.product === 'Pension'),
    },
    {
      displayName: 'Properties',
      id: 'property',
      assets: assets.filter(a => a.product === 'Property'),
    },
    {
      displayName: 'Collectables and other valuables',
      id: 'collectible',
      assets: assets.filter(a => a.product === 'Collectible'),
    },
    {
      displayName: 'Crypto',
      id: 'crypto',
      assets: assets.filter(a => a.product === 'Crypto'),
    },
  ];

  return (
    <Accordion.Item role="region" key={accordionKey} value={accordionKey}>
      <Accordion.Control>
        <AccordionHeader
          title={title}
          count={assets.length}
          amount={addAssets(assets)}
          currency={'GBP'}
          Icon={IconCoins}
          isOpen={activeItem === accordionKey}
          badges={<AssetFeedBadges items={assets} />}
        />
      </Accordion.Control>
      <Accordion.Panel>
        {productSections.map(item => (
          <NetWorthTableSubCategorySection
            key={item.displayName}
            id={item.id}
            onClick={() =>
              setSelectedSection(item.displayName !== selectedSection ? item.displayName : null)
            }
            title={item.displayName}
            expanded={selectedSection === item.displayName}
            total={addAssets(item.assets)}
            badges={<AssetFeedIcons items={item.assets} />}
          >
            {item.displayName === 'Pensions' ? (
              <PensionsTable
                financialItems={item.assets as PensionAsset[]}
                pl="xxs"
                titleParts={[title, item.displayName]}
                onSubmit={onSubmit}
                metadata={metadata}
                onRowClick={onRowClick}
              />
            ) : (
              <FinancialAssetsTable
                financialItems={item.assets}
                pl="xxs"
                titleParts={[title, item.displayName]}
                onSubmit={onSubmit}
                metadata={metadata}
                onRowClick={onRowClick}
              />
            )}
          </NetWorthTableSubCategorySection>
        ))}
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export function addAssets(items: FinancialAsset[]) {
  return items.reduce((accumulator, i) => accumulator + i.value, 0);
}
